import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useParams
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaBars,
  FaMapMarkedAlt,
  FaTimes,
} from "react-icons/fa";
import Home from "./components/home";
import logo from "./images/cape.png";
import PreviewMap from "./components/previewMap";
import PreviewMapForm from "./components/previewMapForm";
import DemoMap from "./components/demoMap";
import LanguageSwitcher from "./components/LanguageSwitcher";
import CaregiverMarketingPage from "./components/caregiverMarketingPage";
import ActivatedMap from "./components/activatedPatientMap";
import PasswordResetPage from "./components/PasswordResetPage";

const LegacyPreviewMapRedirect = ({ includeLocationStep = false }) => {
  const { caregiverID = "" } = useParams();
  const location = useLocation();
  const suffix = includeLocationStep ? "/location" : "";

  return (
    <Navigate
      to={`/realMap/edit/${caregiverID}${suffix}${location.search || ""}`}
      replace
    />
  );
};

const AppContent = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const workerId = "";
  const isMarketingPage = location.pathname === "/caregiver-marketing";

  const navLinks = [
    { label: "Home", to: "/" }
  ];

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const closeOutsideHeader = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOutsideHeader);

    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOutsideHeader);
    };
  }, [menuOpen]);

  return (
    <>
      <style>{responsiveStyles}</style>
      <a className="skip-link" href="#caregiver-main-content">Skip to content</a>

      {!isMarketingPage && (
        <header ref={headerRef} style={headerWrap}>
          <div style={headerShell} className="app-header-shell">
            <Link
              to="/"
              style={brandLink}
              onClick={() => setMenuOpen(false)}
            >
              <div style={logoWrap} className="app-logo-wrap">
                <img src={logo} alt="UberCARE logo" style={logoStyle} />
              </div>

              <div className="app-brand-copy">
                <div style={brandTitle} className="app-brand-title">{t("nav.brand")}</div>
                <div style={brandSubtitle} className="app-brand-subtitle">Caregiver care network</div>
              </div>
            </Link>

            <button
              type="button"
              style={menuToggle}
              className="app-mobile-toggle"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="caregiver-mobile-menu"
            >
              <span className="app-mobile-toggle-icon">
                {menuOpen ? <FaTimes /> : <FaBars />}
              </span>
              <span className="app-mobile-toggle-label">
                {menuOpen ? "Close" : "Menu"}
              </span>
            </button>

            <div
              id="caregiver-mobile-menu"
              style={navArea}
              className={`app-nav-area${menuOpen ? " app-nav-open" : ""}`}
            >
              <nav style={navLinksWrap} aria-label="Caregiver navigation">
                {navLinks.map((item) => {
                  const active =
                    item.to === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(item.to);

                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`app-nav-link${active ? " app-nav-link-active" : ""}`}
                      onClick={(e) => {
                        setMenuOpen(false);
                        if (item.onClick) item.onClick(e);
                      }}
                      style={{
                        ...linkStyle,
                        ...(active ? activeLinkStyle : {})
                      }}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>

              <div style={actionWrap}>
                <LanguageSwitcher userId={workerId} role="worker" />
              </div>
            </div>
          </div>
        </header>
      )}

      {!isMarketingPage && (
        <section
          id="caregiver-map-choices"
          className="caregiver-map-chooser"
          aria-labelledby="caregiver-map-chooser-title"
        >
          <div className="caregiver-map-chooser-copy">
            <span>Choose your experience</span>
            <strong id="caregiver-map-chooser-title">Where would you like to go?</strong>
          </div>

          <div className="caregiver-map-choice-actions">
            <Link
              to="/realMap"
              className={`caregiver-map-choice caregiver-map-choice-real${
                location.pathname.startsWith("/realMap") ? " caregiver-map-choice-active" : ""
              }`}
              aria-current={location.pathname.startsWith("/realMap") ? "page" : undefined}
            >
              <FaMapMarkedAlt aria-hidden="true" />
              <span>
                <strong>Real profile map</strong>
                <small>Find, claim, or update your caregiver profile</small>
              </span>
              <em>Recommended</em>
            </Link>

            <Link
              to="/previewMap"
              className={`caregiver-map-choice caregiver-map-choice-demo${
                location.pathname.startsWith("/previewMap") ? " caregiver-map-choice-active" : ""
              }`}
              aria-current={location.pathname.startsWith("/previewMap") ? "page" : undefined}
            >
              <FaMapMarkedAlt aria-hidden="true" />
              <span>
                <strong>Practice demo</strong>
                <small>Learn how the map works without changing a profile</small>
              </span>
            </Link>
          </div>
        </section>
      )}

      <main
        id="caregiver-main-content"
        className="app-main"
        style={isMarketingPage ? marketingContentOffset : standardContentOffset}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/caregiver-marketing"
            element={<CaregiverMarketingPage />}
          />
          <Route path="/remoteLogin" element={<Navigate to="/realMap" replace />} />
          <Route path="/remoteRegister" element={<Navigate to="/realMap" replace />} />
          <Route path="/logout" element={<Navigate to="/realMap" replace />} />

          <Route
            path="/remoteProfileViewer"
            element={<Navigate to="/realMap" replace />}
          />

          <Route
            path="/remoteProfileEdit"
            element={<Navigate to="/realMap" replace />}
          />

          <Route
            path="/workerHome"
            element={<Navigate to="/realMap" replace />}
          />

          <Route
            path="/activatedElderMap"
            element={<Navigate to="/realMap" replace />}
          />
          <Route path="/appointments/:caregiverID" element={<ActivatedMap />} />
          <Route path="/forgot-password" element={<PasswordResetPage />} />

          <Route path="/previewMap" element={<DemoMap />} />
          <Route path="/realMap" element={<PreviewMap />} />
          <Route path="/realMap/add" element={<Navigate to="/realMap" replace />} />
          <Route path="/realMap/add/location" element={<Navigate to="/realMap" replace />} />
          <Route path="/realMap/edit/:caregiverID" element={<PreviewMapForm />} />
          <Route
            path="/realMap/edit/:caregiverID/location"
            element={<PreviewMapForm />}
          />
          <Route path="/previewMap/add" element={<Navigate to="/realMap" replace />} />
          <Route
            path="/previewMap/add/location"
            element={<Navigate to="/realMap" replace />}
          />
          <Route
            path="/previewMap/edit/:caregiverID"
            element={<LegacyPreviewMapRedirect />}
          />
          <Route
            path="/previewMap/edit/:caregiverID/location"
            element={<LegacyPreviewMapRedirect includeLocationStep />}
          />

          <Route
            path="/workermap"
            element={<Navigate to="/realMap" replace />}
          />
        </Routes>
        <footer className="global-footer" aria-label="UberCARE product information">
          <strong>UberCARE for Caregivers</strong>
          <span>Professional care visibility built for a global community.</span>
          <Link className="global-footer-link" to="/caregiver-marketing">
            Why UberCARE
          </Link>
          <span>© {new Date().getFullYear()} UberCARE</span>
        </footer>
      </main>
    </>
  );
};

const App = () => {
  return (
    <Router basename="/worker">
      <AppContent />
    </Router>
  );
};

const responsiveStyles = `
  .app-main {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(255, 210, 74, 0.16), transparent 24%),
      linear-gradient(180deg, #eef5f8 0%, #dfe9f2 100%);
  }

  .skip-link {
    position: fixed;
    top: 8px;
    left: 12px;
    z-index: 5000;
    padding: 12px 16px;
    border-radius: 12px;
    background: #ffffff;
    color: #123a59;
    font-weight: 900;
    box-shadow: 0 12px 28px rgba(12, 42, 68, 0.22);
    transform: translateY(-160%);
    transition: transform 0.16s ease;
  }

  .skip-link:focus {
    transform: translateY(0);
  }

  .global-footer {
    width: min(calc(100% - 24px), 1180px);
    margin: 28px auto 0;
    padding: 18px 20px calc(18px + env(safe-area-inset-bottom));
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px 18px;
    flex-wrap: wrap;
    border-top: 1px solid rgba(14, 85, 128, 0.14);
    color: #58718a;
    font-size: 13px;
    text-align: center;
  }

  .global-footer strong {
    color: #123a59;
  }

  .global-footer-link {
    min-height: 42px;
    padding: 0 13px;
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    color: #174d73;
    background: rgba(31, 98, 143, 0.08);
    border: 1px solid rgba(31, 98, 143, 0.12);
    font-weight: 800;
    text-decoration: none;
  }

  .global-footer-link:hover,
  .global-footer-link:focus-visible {
    background: rgba(241, 196, 15, 0.18);
    border-color: rgba(241, 196, 15, 0.32);
  }

  .caregiver-map-chooser {
    width: min(calc(100% - 24px), 1180px);
    margin: 10px auto 0;
    padding: 12px;
    display: grid;
    grid-template-columns: minmax(180px, 0.42fr) minmax(0, 1.58fr);
    align-items: center;
    gap: 12px;
    border: 1px solid rgba(255, 255, 255, 0.82);
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 16px 38px rgba(17, 56, 84, 0.12);
  }

  .caregiver-map-chooser-copy {
    display: grid;
    gap: 3px;
    padding: 0 7px;
    color: #123a59;
  }

  .caregiver-map-chooser-copy span {
    color: #58718a;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .caregiver-map-chooser-copy strong {
    font-size: 16px;
    line-height: 1.2;
  }

  .caregiver-map-choice-actions {
    display: grid;
    grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
    gap: 9px;
  }

  .caregiver-map-choice {
    position: relative;
    min-width: 0;
    min-height: 68px;
    padding: 10px 12px;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    border-radius: 17px;
    text-decoration: none;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  }

  .caregiver-map-choice > svg {
    font-size: 20px;
  }

  .caregiver-map-choice > span {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  .caregiver-map-choice strong,
  .caregiver-map-choice small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .caregiver-map-choice strong {
    font-size: 14px;
  }

  .caregiver-map-choice small {
    font-size: 11px;
    line-height: 1.25;
  }

  .caregiver-map-choice em {
    padding: 5px 7px;
    border-radius: 999px;
    background: rgba(23, 50, 74, 0.12);
    font-size: 9px;
    font-style: normal;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .caregiver-map-choice-real {
    border: 1px solid #e5b900;
    background: linear-gradient(135deg, #ffd33d, #f1c40f);
    color: #17324a;
    box-shadow: 0 10px 22px rgba(241, 196, 15, 0.22);
  }

  .caregiver-map-choice-demo {
    border: 1px solid rgba(31, 98, 143, 0.18);
    background: linear-gradient(180deg, #ffffff, #edf5fa);
    color: #174d73;
  }

  .caregiver-map-choice-active {
    box-shadow: 0 0 0 3px #ffffff, 0 0 0 6px rgba(31, 98, 143, 0.3);
  }

  @media (hover: hover) and (pointer: fine) {
    .caregiver-map-choice:hover {
      transform: translateY(-2px);
      box-shadow: 0 15px 28px rgba(17, 56, 84, 0.18);
    }
  }

  .app-mobile-toggle {
    display: none !important;
  }

  .app-mobile-toggle-label {
    display: none;
  }

  .app-nav-area a,
  .app-nav-area button {
    min-height: 42px;
  }

  .app-nav-area nav {
    order: 1;
  }

  .app-nav-area > div {
    order: 2;
  }

  @media (max-width: 980px) {
    .caregiver-map-chooser {
      grid-template-columns: 1fr;
    }

    .app-header-shell {
      position: relative;
      align-items: center !important;
      flex-wrap: nowrap !important;
    }

    .app-mobile-toggle {
      display: inline-flex !important;
    }

    .app-nav-area {
      position: absolute !important;
      top: calc(100% + 8px);
      left: 0;
      display: none !important;
      width: 100%;
      flex-direction: column !important;
      gap: 12px !important;
      max-height: calc(100dvh - 94px);
      padding: 12px !important;
      overflow-y: auto;
      overscroll-behavior: contain;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 20px;
      background: linear-gradient(150deg, rgba(8, 50, 79, 0.99), rgba(15, 88, 128, 0.99));
      box-shadow: 0 24px 54px rgba(8, 34, 55, 0.34);
    }

    .app-nav-open {
      display: flex !important;
    }

    .app-nav-area nav,
    .app-nav-area > div {
      width: 100% !important;
      justify-content: stretch !important;
    }

    .app-nav-area nav {
      order: 1;
    }

    .app-nav-area > div {
      order: 2;
    }

    .app-nav-area a,
    .app-nav-area button {
      flex: 1 1 180px !important;
      justify-content: center !important;
      background: rgba(255, 255, 255, 0.12) !important;
      color: #ffffff !important;
      border: 1px solid rgba(255, 255, 255, 0.18) !important;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
    }

    .app-nav-link-active {
      background: #ffd24a !important;
      color: #14324c !important;
      border-color: #ffe58b !important;
      box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18) !important;
    }

    .app-language-switcher {
      width: 100% !important;
      min-height: 50px !important;
      margin: 0 !important;
      border-radius: 12px !important;
      background: rgba(255, 255, 255, 0.12) !important;
      color: #ffffff !important;
      font-size: 15px !important;
      font-weight: 800 !important;
    }
  }

  @media (max-width: 560px) {
    .caregiver-map-chooser {
      width: min(calc(100% - 12px), 1180px);
      margin-top: 7px;
      padding: 9px;
      border-radius: 19px;
    }

    .caregiver-map-chooser-copy {
      padding: 2px 4px 0;
    }

    .caregiver-map-choice-actions {
      grid-template-columns: 1fr;
    }

    .caregiver-map-choice {
      min-height: 66px;
    }

    .app-header-shell {
      min-height: 64px;
      padding: 8px 9px !important;
      border-radius: 18px !important;
      gap: 8px !important;
    }

    .app-logo-wrap {
      width: 46px !important;
      height: 46px !important;
      border-radius: 12px !important;
    }

    .app-logo-wrap img {
      width: 34px !important;
      height: 34px !important;
    }

    .app-brand-copy {
      min-width: 0;
    }

    .app-brand-title {
      max-width: 48vw;
      font-size: 16px !important;
      line-height: 1.1 !important;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .app-brand-subtitle {
      display: none !important;
    }

    .app-mobile-toggle {
      width: auto !important;
      min-width: 78px;
      height: 48px !important;
      gap: 7px;
      padding: 0 12px;
      background: #ffd24a !important;
      color: #14324c !important;
      border: 1px solid rgba(255,255,255,0.58) !important;
      box-shadow: 0 10px 22px rgba(0,0,0,0.22) !important;
    }

    .app-mobile-toggle-label {
      display: inline !important;
      font-size: 14px;
      font-weight: 900;
    }

    .app-nav-area {
      gap: 10px !important;
      padding: 10px !important;
    }

    .app-nav-area nav,
    .app-nav-area > div {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
    }

    .app-nav-area a,
    .app-nav-area button {
      width: 100% !important;
      min-height: 50px !important;
      border-radius: 12px !important;
      font-size: 15px !important;
      font-weight: 900 !important;
    }
  }

  @media (max-width: 380px) {
    .app-brand-title {
      font-size: 15px !important;
    }
  }
`;

const headerWrap = {
  position: "sticky",
  top: "0",
  width: "min(1180px, calc(100% - 24px))",
  margin: "12px auto 0",
  zIndex: 1000
};

const headerShell = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "9px 12px",
  borderRadius: "999px",
  background: "linear-gradient(135deg, rgba(9, 52, 82, 0.98), rgba(15, 88, 128, 0.97))",
  border: "1px solid rgba(255, 255, 255, 0.24)",
  boxShadow: "0 22px 48px rgba(8, 34, 55, 0.22)",
  backdropFilter: "blur(18px)"
};

const brandLink = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  textDecoration: "none",
  minWidth: 0
};

const logoWrap = {
  width: "58px",
  height: "58px",
  borderRadius: "17px",
  background: "rgba(255, 255, 255, 0.14)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0
};

const logoStyle = {
  width: "46px",
  height: "46px",
  objectFit: "contain"
};

const brandTitle = {
  color: "#ffd33d",
  fontWeight: "800",
  fontSize: "20px",
  lineHeight: 1
};

const brandSubtitle = {
  marginTop: "4px",
  color: "rgba(255, 255, 255, 0.72)",
  fontSize: "11px",
  fontWeight: "700",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const menuToggle = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  border: "1px solid rgba(255,255,255,0.34)",
  background: "#ffd24a",
  color: "#14324c",
  cursor: "pointer",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
  flexShrink: 0
};

const navArea = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "14px",
  flex: 1,
  minWidth: 0
};

const navLinksWrap = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap"
};

const linkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  textDecoration: "none",
  color: "rgba(255, 255, 255, 0.86)",
  padding: "8px 12px",
  borderRadius: "14px",
  fontSize: "13px",
  fontWeight: "700",
  background: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.1)"
};

const activeLinkStyle = {
  color: "#ffffff",
  background: "rgba(255, 255, 255, 0.14)"
};

const actionWrap = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  justifyContent: "flex-end"
};

const standardContentOffset = {
  paddingTop: "12px"
};

const marketingContentOffset = {
  paddingTop: 0
};

export default App;
